import moment from 'moment';
import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Space, Table, Tooltip } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Button from 'components/new-ui/base/button/Button';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import Notification from 'components/ui/base/notification/Notification';
import useSession from 'hooks/useSession';
import { listManifestEnsDeclarations } from 'store/manifests/client';
import { archiveDeclaration, deleteDeclaration, unarchiveDeclaration } from 'store/declarations/client';
import {
    getIcs2RowClassName,
    hasIcs2Notifications,
    Ics2ListRow,
    Ics2TableRow,
    isIcs2GroupRow,
    toIcs2TableRows,
} from 'store/declarations/eu/ics2/ics2-declaration-list';
import { ListPayload } from 'core/http/response';
import { getIcs2Columns } from 'views/custom-declaration/declaration-table/ics2Columns';
import ArchiveDeclarationModal from 'views/custom-declaration/declaration-table/components/ArchiveDeclarationModal';
import UnarchiveDeclarationModal from 'views/custom-declaration/declaration-table/components/UnarchiveDeclarationModal';
import DeleteDeclarationModal from 'views/custom-declaration/declaration-table/components/DeleteDeclarationModal';
import useIcs2GroupActions from 'views/custom-declaration/declaration-table/components/useIcs2GroupActions';
import { getExpandableConfig } from 'components/ui/base/table/expandedTableConfig';
import { getErrorMessage } from 'utils/getErrorMessage';
import { runWithNotification } from 'utils/runWithNotification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import ArchiveFilter, { ArchiveFilterValue } from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import { useHandleTableChange } from 'utils/tableHelpers';
import { declarationHasBeenSubmitted } from 'views/declarations/utils/form-utils';
import ManifestArrivalNotificationSection from '../components/ManifestArrivalNotificationSection';

type ModalState =
    | { type: 'none' }
    | { type: 'archive'; ids: string[] }
    | { type: 'unarchive'; ids: string[] }
    | { type: 'delete'; id: string };

const ManifestEns = () => {
    const { manifestId } = useParams<{ manifestId: string }>();
    const { userInfo } = useSession();
    const navigate = useNavigate();

    const [ensDeclarations, setEnsDeclarations] = useState<ListPayload<Ics2ListRow>>();
    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState<ModalState>({ type: 'none' });
    const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

    const { query, filters, page, pageSize, onSearch, setFilter, onTableChange, searchParams } = useHandleTableChange({
        filterKeys: ['archived', 'exactDate'] as const,
    });

    // The debounced search reaches the URL first, so reading it back also applies a shared link's search.
    const search = searchParams.get(`query`) ?? undefined;
    const exactDate: string | undefined = filters?.exactDate ?? undefined;
    const archived = filters?.archived === 'true';

    const fetchDeclarations = useCallback(async () => {
        if (!manifestId) return;

        setIsLoading(true);
        try {
            setEnsDeclarations(
                await listManifestEnsDeclarations(manifestId, {
                    page,
                    size: pageSize,
                    query: search,
                    exactDate,
                    archived,
                })
            );
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsLoading(false);
        }
    }, [manifestId, page, pageSize, search, exactDate, archived]);

    useEffect(() => {
        fetchDeclarations();
    }, [fetchDeclarations]);

    const runAction = async (action: () => Promise<unknown>, success: string, failure: string) => {
        setModal({ type: 'none' });
        if (await runWithNotification(action, { success, failure })) await fetchDeclarations();
    };

    const declarations = useMemo(() => toIcs2TableRows(ensDeclarations?.list), [ensDeclarations]);

    useEffect(() => {
        setExpandedRowKeys([]);
    }, [ensDeclarations]);

    /** Opens a declaration on the view its status calls for, the way the declarations dashboard does. */
    const navigateToDeclaration = (record: Ics2TableRow) => {
        let pathSuffix = '';

        if ((record.status && declarationHasBeenSubmitted(record.status)) || archived) {
            pathSuffix = '/view-only';
        }

        navigate(`/declarations/${record.id}${pathSuffix}`);
    };

    // A filter re-lists from scratch, so the page held from the previous result would land out of range.
    const applyFilter = (key: 'archived' | 'exactDate', value?: string) => {
        setFilter(key, value);
        onTableChange({ current: 1, pageSize });
    };

    const hasGroups = declarations.some(isIcs2GroupRow);

    const { groupActions, groupActionModals } = useIcs2GroupActions({ onDone: fetchDeclarations });

    const columns = getIcs2Columns(
        {
            onArchive: (ids: string[]) => setModal({ type: 'archive', ids }),
            onUnarchive: (ids: string[]) => setModal({ type: 'unarchive', ids }),
            onDelete: (id: string) => setModal({ type: 'delete', id }),
            groupActions,
        },
        userInfo,
        { hasGroups, hasNotifications: hasIcs2Notifications(declarations) }
    );

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Row justify="space-between">
                <Col>
                    <Space size={8}>
                        <SearchBar inputPlaceholder="Search" value={query.current ?? ''} onChange={onSearch} />
                        <Tooltip title="Filters are not available yet.">
                            <span>
                                <Button disabled>Filter</Button>
                            </span>
                        </Tooltip>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <ArchiveFilter
                            value={archived ? 'archived' : 'active'}
                            onChange={(value: ArchiveFilterValue) =>
                                applyFilter('archived', value === 'archived' ? 'true' : 'false')
                            }
                        />
                        <DatePicker
                            value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                            onChange={(val) => applyFilter('exactDate', val ? val.format('YYYY-MM-DD') : undefined)}
                        />
                    </Space>
                </Col>
            </Row>

            <StyledTableWrapper>
                <Table<Ics2TableRow>
                    loading={isLoading}
                    rowKey="id"
                    dataSource={declarations}
                    columns={columns}
                    rowClassName={(record) => getIcs2RowClassName(record, expandedRowKeys)}
                    expandable={
                        hasGroups
                            ? {
                                  ...getExpandableConfig<Ics2TableRow>((record) => !!record?.children?.length),
                                  expandedRowKeys,
                                  onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                              }
                            : undefined
                    }
                    components={{
                        header: {
                            cell: (props: any) => <StyledTableHeaderCell {...props} />,
                        },
                    }}
                    onRow={(record) => ({
                        'data-testid': 'ens-declaration-table-row',
                        style: { cursor: 'pointer' },
                        onClick: () => {
                            // A group row summarises its declarations rather than being one of them,
                            // so it has nothing of its own to open.
                            if (isIcs2GroupRow(record)) return;
                            navigateToDeclaration(record);
                        },
                    })}
                    onChange={onTableChange}
                    pagination={{
                        current: (ensDeclarations?.pageNumber ?? 0) + 1,
                        pageSize: ensDeclarations?.pageSize ?? pageSize,
                        total: ensDeclarations?.total,
                        showSizeChanger: true,
                        hideOnSinglePage: true,
                        position: ['bottomCenter'],
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </StyledTableWrapper>

            <ManifestArrivalNotificationSection manifestId={manifestId} />

            <ArchiveDeclarationModal
                open={modal.type === 'archive'}
                onCancel={() => setModal({ type: 'none' })}
                onConfirm={() => {
                    if (modal.type !== 'archive') return;
                    const { ids } = modal;
                    const noun = ids.length === 1 ? 'Declaration' : 'Declarations';
                    runAction(
                        () => Promise.all(ids.map((id) => archiveDeclaration(id))),
                        `${noun} archived successfully!`,
                        `Could not archive the ${noun.toLowerCase()}!`
                    );
                }}
            />

            <UnarchiveDeclarationModal
                open={modal.type === 'unarchive'}
                onCancel={() => setModal({ type: 'none' })}
                onConfirm={() => {
                    if (modal.type !== 'unarchive') return;
                    const { ids } = modal;
                    const noun = ids.length === 1 ? 'Declaration' : 'Declarations';
                    runAction(
                        () => Promise.all(ids.map((id) => unarchiveDeclaration(id))),
                        `${noun} unarchived successfully!`,
                        `Could not unarchive the ${noun.toLowerCase()}!`
                    );
                }}
            />

            <DeleteDeclarationModal
                open={modal.type === 'delete'}
                onCancel={() => setModal({ type: 'none' })}
                onDelete={() => {
                    if (modal.type !== 'delete') return;
                    const { id } = modal;
                    runAction(
                        () => deleteDeclaration(id),
                        'Declaration deleted successfully!',
                        'Could not delete the declaration!'
                    );
                }}
            />

            {groupActionModals}
        </Space>
    );
};

export default ManifestEns;
